// Intl formatters are hoisted/memoized at module scope: each `new Intl.NumberFormat`
// loads and allocates locale-data tables that are expensive to rebuild per call —
// currencyFormat runs per product card (up to ~80x per grid render), so this stays hot.
const KRW_FORMATTER = new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' });
const genericCurrencyFormatters = new Map();

function getCurrencyFormatter(currency) {
    let formatter = genericCurrencyFormatters.get(currency);
    if (!formatter) {
        formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
        genericCurrencyFormatters.set(currency, formatter);
    }
    return formatter;
}

/**
 * Format a price for the current user locale.
 * @param number a Number or BigInt to format
 * @return {string} the formatted string
 */
export function currencyFormat(number, currency) {
    return currency === 'KRW' ?
        // specialized formatting to match standard korean won format
        KRW_FORMATTER.format(number).replace(/[^0-9,.]*([0-9,.]+)[^0-9,.]*/, '$1원') :
        // generic currency formatting
        getCurrencyFormatter(currency).format(number);
}

/**
 * Format a date for the current user locale.
 * @param date the date to format
 * @param dateStyle the date style, defaults to medium
 * @param timeStyle the time style, default to hidden
 * @return {string} the formatted string
 */
export function dateFormat(date, dateStyle = 'medium', timeStyle = undefined) {
    return new Intl.DateTimeFormat(
        undefined,
        {
            dateStyle: dateStyle,
            timeStyle: timeStyle
        }
    ).format(date);
}

/**
 * Format a count number with 'k' suffix for thousands (e.g. 1500 → "1.5k").
 * @param count a Number to format
 * @return {string} the formatted string
 */
export function countFormat(count) {
    if (count >= 1000) return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return String(count);
}

/**
 * Format a compact number for the current user locale.
 * @param number a Number or BigInt to format
 * @return {string} the formatted string
 */
export function numberCompactFormat(number) {
    return new Intl.NumberFormat(
        undefined,
        {
            notation: 'compact'
        }
    ).format(number);
}

/**
 * Format a date as a deterministic YYYY.MM.DD label.
 * @param value a Date-compatible value
 * @return {string} the formatted string
 */
export function stableDateLabel(value) {
    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    try {
        const formatter = new Intl.DateTimeFormat('ko-KR', {
            timeZone: 'Asia/Seoul',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });

        return formatter
            .format(date)
            .replace(/\.\s/g, '.')
            .replace(/\.$/, '');
    } catch (error) {
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');

        return `${year}.${month}.${day}`;
    }
}

/**
 * Format a relative time for the current user locale.
 * @param date the date to format
 * @return {string} the formatted string
 */
export function timeagoFormat(date) {
    const relative_time = date.getTime() - Date.now();
    const relative_units = [
        {name: 'year', duration: 365 * 24 * 60 * 60 * 1000},
        {name: 'month', duration: 30 * 24 * 60 * 60 * 1000},
        {name: 'week', duration: 7 * 24 * 60 * 60 * 1000},
        {name: 'day', duration: 24 * 60 * 60 * 1000},
        {name: 'hour', duration: 60 * 60 * 1000},
        {name: 'minute', duration: 60 * 1000},
        {name: 'second', duration: 1000}
    ];

    const largest_unit = relative_units.find(unit => Math.abs(relative_time) > unit.duration) ||
        relative_units[relative_units.length - 1];

    return new Intl.RelativeTimeFormat()
        .format(Math.round(relative_time / largest_unit.duration), largest_unit.name);
}
